import { UserType } from '../../../interfaces/userType.enum';
import { ModuleKey } from './types';

export type PresetKey = 'owner' | 'manager' | 'accountant' | 'basic';

// Ordered widget ids per preset. The Action Center is pinned separately and is not listed here.
//
// `owner` is the premium business-owner board (unlocked per-institution by a SaaS Operator and then
// granted per-user). `basic` is the free minimal board an InstitutionAdmin sees until the premium
// board is unlocked — a single core widget plus the pinned Action Center.
export const ROLE_PRESETS: Record<PresetKey, string[]> = {
    // Whole-business board: today's workforce pulse, then leave, people and payroll. The headline
    // numbers live in the KPI strip (BoardKpiStrip) above the grid. md widgets come in pairs so a
    // row never leaves a blank half; widgets the role or the institution's modules exclude are
    // filtered out at render time.
    owner: [
        'attendance.dayPulse',
        'attendance.trend', 'leave.summary',
        'people.summary', 'payroll.byDepartment',
        'payroll.trend',
        'attendance.department',
    ],
    // Operations board for an institution manager: the workforce first.
    manager: [
        'attendance.dayPulse',
        'attendance.trend', 'leave.summary',
        'people.summary', 'leave.byType',
        'attendance.department',
    ],
    // Payroll home for the accountant (attendance and leave widgets are not theirs to see).
    accountant: [
        'payroll.trend',
        'payroll.byDepartment', 'payroll.overtime',
        'people.growth',
    ],
    basic: ['attendance.dayPulse'],
};

// Role → default board when the user has no explicit DASHBOARD_VIEW_* grants.
// The owner board is NEVER a role default: it is premium-gated and granted per-user, so an
// InstitutionAdmin falls back to the free `basic` board until the premium board is unlocked and granted.
export function defaultPresetForRoles(roles: UserType[]): PresetKey {
    if (roles.includes(UserType.Accountant)) return 'accountant';
    if (roles.includes(UserType.InstitutionManager)) return 'manager';
    return 'basic';
}

// Default widget order for a module's deep board. `ModuleBoard` uses the matching array as its
// fallback layout when the user has no saved layout for that board_key. md widgets are paired so a
// row never leaves a blank half; `holidays` has no deep board.
export const MODULE_DEFAULT_PRESETS: Record<ModuleKey, string[]> = {
    attendance: [
        'attendance.kpis', 'attendance.dayPulse', 'attendance.composition', 'attendance.arrivals',
        'attendance.weekday', 'attendance.topLate', 'attendance.topOt', 'attendance.department',
        'attendance.todayLog',
    ],
    leave: ['leave.summary', 'leave.byType', 'leave.trend'],
    // Workforce overview (the HR manager's board): today's pulse, headcount, leave approvals, then
    // trends. Attendance/leave widgets hide themselves for roles that can't see them.
    people: [
        'attendance.dayPulse',
        'people.summary', 'leave.summary',
        'attendance.trend', 'leave.byType',
        'people.growth',
        'attendance.department',
    ],
    holidays: [],
    // Accounting lives on its own pages (/accounting), not on a dashboard board.
    accounting: [],
    // The run's headline numbers are in the KPI strip, so the numbers-only payroll.summary widget
    // stays in the picker but not in the default.
    payroll: ['payroll.trend', 'payroll.byDepartment', 'payroll.overtime'],
};
